import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { LibraryModule } from '../shared/lib/lib.module';

@Component({
  selector: 'app-error-fallback',
  standalone: true,
  imports: [CommonModule, LibraryModule],
  templateUrl: './error-fallback.component.html',
  styleUrls: ['./error-fallback.component.css'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ 
          opacity: 0,
          transform: 'scale(0.9) translateZ(0)'
        }),
        animate('400ms ease-out', style({ 
          opacity: 1,
          transform: 'scale(1) translateZ(0)'
        }))
      ])
    ])
  ]
})
export class ErrorFallbackComponent implements OnInit {
  private readonly router = inject(Router);
  
  errorType: 'network' | 'server' | 'unknown' = 'unknown';
  errorMessage: string = '';
  isRetrying: boolean = false;

  ngOnInit(): void {
    // Get error details from navigation state or query params
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;
    
    if (state?.['error']) {
      const error = state['error'];
      this.determineErrorType(error);
    } else {
      // Check query params
      const urlParams = new URLSearchParams(window.location.search);
      const errorParam = urlParams.get('error');
      if (errorParam) {
        this.errorType = errorParam as 'network' | 'server' | 'unknown';
      }
    }
    
    this.setErrorMessage();
  }

  private determineErrorType(error: any): void {
    if (!error) {
      this.errorType = 'unknown';
      return;
    }

    // Check for network errors
    if (error.status === 0 || 
        error.message?.toLowerCase().includes('network') ||
        error.message?.toLowerCase().includes('failed to fetch') ||
        error.message?.toLowerCase().includes('internet') ||
        error.name === 'HttpErrorResponse' && error.status === 0) {
      this.errorType = 'network';
    }
    // Check for server errors (5xx)
    else if (error.status >= 500 && error.status < 600) {
      this.errorType = 'server';
    }
    // Check for timeout errors
    else if (error.status === 408 || error.message?.toLowerCase().includes('timeout')) {
      this.errorType = 'network';
    }
    else {
      this.errorType = 'unknown';
    }
  }

  private setErrorMessage(): void {
    switch (this.errorType) {
      case 'network':
        this.errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        break;
      case 'server':
        this.errorMessage = 'The server is experiencing issues. Please try again later.';
        break;
      default:
        this.errorMessage = 'An unexpected error occurred. Please try again.';
    }
  }

  retry(): void {
    this.isRetrying = true;
    
    // Try to go back to previous page, or go to home
    setTimeout(() => {
      this.router.navigate(['/']).then(() => {
        // Reload the page to retry
        window.location.reload();
      });
    }, 500);
  }

  goHome(): void {
    this.router.navigate(['/']);
  }
}

